import { ShieldCheck, Upload, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';

import { useDefaultWaiverAvailable, useCompanyHasWaiver, useApplyDefaultWaiver } from '../../../hooks/useDefaultWaiver';

interface WaiverSetupWizardProps {
  companyId: string;
  onComplete: () => void;
  onBack: () => void;
  onSkip: () => void;
}

export function WaiverSetupWizard({ companyId, onComplete, onBack, onSkip }: WaiverSetupWizardProps) {
  const { data: defaultWaiver, isLoading: isLoadingDefault } = useDefaultWaiverAvailable();
  const { data: hasExistingWaiver, isLoading: isLoadingExisting } = useCompanyHasWaiver(companyId);
  const { apply, isLoading: isApplying, error: applyError } = useApplyDefaultWaiver();
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const isLoading = isLoadingDefault || isLoadingExisting;

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleUseDefault = async () => {
    try {
      await apply(companyId);
      setNotification({ type: 'success', message: 'Default waiver template applied successfully!' });
      setTimeout(onComplete, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to apply default waiver';
      setNotification({ type: 'error', message });
    }
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
        <div className="flex items-center justify-center py-8">
          <Loader2 size={32} className="text-rollops-accent animate-spin" />
        </div>
      </div>
    );
  }

  // If company already has a waiver configured
  if (hasExistingWaiver) {
    return (
      <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
        {/* Notification Toast */}
        {notification && (
          <div
            className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg ${
              notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}
          >
            {notification.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="font-medium">{notification.message}</span>
          </div>
        )}

        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-[#EBFFEE]">Waiver Already Set Up</h2>
          <p className="text-[#A9A3A3] mt-2">
            Your gym already has a liability waiver template configured. You can manage it from the Damage Waivers page in your dashboard.
          </p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={onBack}
            className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors"
          >
            Back
          </button>
          <button
            onClick={onComplete}
            className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-semibold rounded-lg transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
      {/* Notification Toast */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg ${
            notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {notification.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span className="font-medium">{notification.message}</span>
        </div>
      )}

      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
          <ShieldCheck size={32} className="text-rollops-accent" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Liability Waiver</h2>
        <p className="text-[#A9A3A3] mt-2">
          Set up a liability waiver for your members to sign. You can use our default template or upload your own.
        </p>
      </div>

      {applyError && (
        <div className="mb-4 p-3 bg-red-900/20 border border-red-600 rounded-lg">
          <p className="text-red-300 text-sm">{applyError.message}</p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {/* Use Default Waiver */}
        {defaultWaiver ? (
          <button
            onClick={handleUseDefault}
            disabled={isApplying}
            className="w-full py-4 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isApplying ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Applying Default Waiver...
              </>
            ) : (
              <>
                <ShieldCheck size={20} />
                Use RollOps Default Waiver
              </>
            )}
          </button>
        ) : (
          <div className="w-full py-4 px-4 bg-[#363232] text-[#A9A3A3] font-semibold rounded-lg text-center">
            <p className="text-sm">No default waiver template available yet.</p>
            <p className="text-xs mt-1">You can upload your own below or skip for now.</p>
          </div>
        )}

        {/* Upload Your Own */}
        <a
          href="/damage-waivers/upload"
          className="w-full py-4 px-4 bg-[#363232] hover:bg-[#504A4A] text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-3 border border-[#504A4A]"
        >
          <Upload size={20} />
          Upload Your Own Waiver
        </a>

        {/* Navigation */}
        <div className="flex gap-4 mt-2">
          <button
            onClick={onBack}
            className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors"
          >
            Back
          </button>
        </div>

        {/* Skip */}
        <button
          onClick={onSkip}
          className="w-full py-3 px-4 bg-transparent hover:bg-[#363232] text-[#A9A3A3] font-semibold rounded-lg transition-colors border border-[#504A4A]"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
